import { XMarkIcon } from '@heroicons/react/20/solid';
import { AnimatePresence, motion } from 'framer-motion';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import useWindowResize from '../../hooks/use-window-resize';

type SidePanelProps = {
    isOpen: boolean;
    children: ReactNode | ReactNode[];
    onClose: () => void;
    panelWidth?: number;
};

function SidePanel({ isOpen, children, onClose, panelWidth = 350 }: SidePanelProps): JSX.Element {
    const { width } = useWindowResize();
    const sidebarNav = useRef<HTMLDivElement>(null);

    const overlaySidebar = width <= 1100;

    // When the window scrolls, recalculate the nav height.
    const handleScroll = useCallback(function handleScroll() {
        if (sidebarNav.current) {
            sidebarNav.current.style.height = `${getNavHeightWithScroll(sidebarNav.current)}px`;
        }
    }, []);

    // Listen for scroll events
    useEffect(() => {
        window.addEventListener('scroll', handleScroll, true);
        return (): void => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [handleScroll]);

    return (
        <div className={overlaySidebar ? 'fixed top-14 right-0 z-50' : ''}>
            <div
                ref={sidebarNav}
                style={{ scrollbarGutter: isOpen ? 'stable' : '', height: getNavHeightWithScroll(sidebarNav.current) }}
                className={`flex flex-col ${
                    overlaySidebar ? 'absolute top-6 right-0 bg-white' : 'ml-8 -mr-8 xl:-mr-16 top-0 sticky'
                } overflow-x-hidden overflow-y-auto min-w-max overscroll-contain`}
            >
                <AnimatePresence initial={false}>
                    {isOpen && (
                        <motion.aside
                            transition={{ duration: 0.2 }}
                            initial={{ minWidth: 0, width: 0 }}
                            animate={{ minWidth: 350, width: panelWidth }}
                            exit={{ minWidth: 0, width: 0 }}
                            className="border-l grow"
                        >
                            <motion.div
                                className="py-2 px-4 h-full"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1, transition: { duration: 0.05, delay: 0.175 } }}
                                exit={{ opacity: 0, transition: { duration: 0 } }}
                            >
                                <button className="absolute top-2 right-4" onClick={onClose}>
                                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                                </button>
                                {children}
                            </motion.div>
                        </motion.aside>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

export default SidePanel;

/**
 * Calculates the height (in pixels) of the provided element to be the remaining height of the screen.
 * @param el HTML Element
 * @returns Computed nav height
 */
function getNavHeightWithScroll(el: Element | null): number {
    const bottomPaddingOffset = 16;
    return window.innerHeight - (el?.getBoundingClientRect()?.y ?? 0) - bottomPaddingOffset;
}
